import { inject, Pipe, PipeTransform } from '@angular/core';
import type { DateTimeSettings } from '@cdw-ae/common-data';
import { DateTimeAngularService } from './date-time-angular.service';

/**
 * TimezoneDateTimePipe formats UTC dates according to provided timezone and format preferences.
 *
 * This pipe is generic and can be used for any timezone-aware date formatting scenario.
 * It requires DateTimeSettings to be passed as a parameter.
 *
 * Usage:
 * {{ utcDate | timezoneDateTime:settings }} - Full date and time
 * {{ utcDate | timezoneDateTime:settings:'date' }} - Date only
 * {{ utcDate | timezoneDateTime:settings:'time' }} - Time only
 *
 * @example
 * ```typescript
 * // In component:
 * settings = getCustomerDateTimeSettings(this.store);
 * ```
 *
 * ```html
 * <!-- Full date and time -->
 * <span>{{ auditLog.createdAt | timezoneDateTime:settings }}</span>
 *
 * <!-- Date only -->
 * <span>{{ device.lastSync | timezoneDateTime:settings:'date' }}</span>
 *
 * <!-- Time only -->
 * <span>{{ event.timestamp | timezoneDateTime:settings:'time' }}</span>
 * ```
 */
@Pipe({
  name: 'timezoneDateTime',
  standalone: true,
})
export class TimezoneDateTimePipe implements PipeTransform {
  private readonly dateTimeService = inject(DateTimeAngularService);

  transform(
    value: string | Date | null | undefined,
    settings: DateTimeSettings,
    format: 'dateTime' | 'date' | 'time' = 'dateTime'
  ): string {
    if (!value) return '';
    if (!settings) return '';

    try {
      switch (format) {
        case 'date':
          return this.dateTimeService.formatDate(value, settings);
        case 'time':
          return this.dateTimeService.formatTime(value, settings.timezone);
        case 'dateTime':
        default:
          return this.dateTimeService.format(value, settings, true);
      }
    } catch {
      return String(value);
    }
  }
}
